/**
 * Holt die Laufzeitkonfiguration des Kundenstacks (ADR-044).
 *
 * **Die Monteur-App erbt den Weg** — so steht es in ADR-044 unter Auswirkung,
 * mit `PWA-001` und `PWA-003` als Adressaten. Bis zum 05.09.2026 lud sie
 * nichts: kein Plugin, kein Abruf, keine Kundendaten zur Laufzeit. Aufgefallen
 * beim Versuch, die Unterlagen im Quellsystem zu öffnen — dafür braucht sie
 * die Adressen der Dienste, und die stehen genau hier.
 *
 * **Warum das neben `apps/portal/src/konfiguration.ts` steht und nicht darin.**
 * Der Vertrag ist geteilt: `lesePortalKonfiguration` aus `packages/config`
 * prüft für beide. Das `fetch` drumherum ist doppelt, und das ist Absicht —
 * `packages/config` gehört `OPS-002` und ist abgeschlossen; ein
 * abgeschlossenes Paket für eine Bequemlichkeit zu öffnen, kostet mehr als
 * dreißig Zeilen an zwei Orten. Wandert die Funktion später doch dorthin, ist
 * es eine benannte Aufgabe mit einem Suchbegriff.
 *
 * **Auflage für später:** Der Service Worker aus `PWA-001` darf diese Datei
 * **nicht** in den Offline-Vorrat aufnehmen. ADR-044 begründet es: Sonst
 * überlebt eine alte Konfiguration das Update, und ein abgeschaltetes Merkmal
 * erscheint weiter — ein Fehlerbild ohne erkennbare Ursache. Einen Service
 * Worker gibt es hier noch nicht; wer ihn baut, findet die Auflage hier.
 */

import {
  lesePortalKonfiguration,
  OHNE_KONFIGURATION,
  type PortalKonfiguration,
} from "@hwc/config";

/** Absolut vom Wurzelpfad: Die App hat Unterrouten. */
const PFAD = "/portal-konfiguration.json";

export interface Ladeergebnis {
  readonly konfiguration: PortalKonfiguration;
  /** Was gefehlt hat. */
  readonly fehlend: readonly string[];
  /** Gesetzt, wenn die Datei gar nicht kam. */
  readonly fehler: string | null;
}

export async function ladeKonfiguration(): Promise<Ladeergebnis> {
  try {
    // `no-store` ist Auflage 4 aus ADR-044, und für diese App die wichtigere
    // Hälfte der Auflage: Ein Gerät, das offline arbeitet, hält Antworten
    // ohnehin länger als gedacht.
    const antwort = await fetch(PFAD, { cache: "no-store" });
    if (!antwort.ok) {
      return {
        konfiguration: OHNE_KONFIGURATION,
        fehlend: [],
        fehler: `${PFAD} antwortet mit ${antwort.status}`,
      };
    }
    const { konfiguration, fehlend } = lesePortalKonfiguration(await antwort.json());
    return { konfiguration, fehlend, fehler: null };
  } catch (ursache) {
    /*
     * **Kein Fehlerfall im Feld.** Ohne Netz kommt die Datei nicht, und die
     * App muss trotzdem laufen — das ist nach `07` §7.9 ihr Normalzustand.
     * Ohne Konfiguration bleibt es bei den Vorgabewerten: keine
     * Dienstadressen, also kein Sprung ins Quellsystem, und das ist die
     * richtige Antwort statt eines Verweises, der ins Leere führt.
     */
    return {
      konfiguration: OHNE_KONFIGURATION,
      fehlend: [],
      fehler: ursache instanceof Error ? ursache.message : String(ursache),
    };
  }
}
